import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, inr, imageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Heart, Plus, Minus, Package, Sparkles, ShieldCheck, Truck, Leaf, Gift } from "lucide-react";
import { toast } from "sonner";

export default function ComboDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const { getItem, addItem, setQty } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let c;
        try {
          c = (await api.get(`/combos/slug/${slug}`)).data;
        } catch {
          c = (await api.get(`/combos/${slug}`)).data;
        }
        setCombo(c);
        setSelectedIdx(c.primary_image_index || 0);
      } catch {
        setCombo(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="py-12 text-center text-hyd-text2">Loading combo…</div>;
  if (!combo) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <p className="text-hyd-text2">Combo not found.</p>
      <Link to="/combos" className="text-hyd-gold underline mt-2 inline-block">All combos</Link>
    </div>
  );

  const images = combo.images?.length ? combo.images : (combo.image_url ? [combo.image_url] : []);
  const currentImg = imageUrl(images[selectedIdx] || images[0] || "");
  const discount = combo.mrp > combo.price ? Math.round(((combo.mrp - combo.price) / combo.mrp) * 100) : 0;
  const inCart = getItem("combo", combo.id);
  const qty = inCart?.qty || 0;
  const items = combo.items_expanded || [];
  const itemsMrp = items.reduce((s, i) => s + (i.product?.mrp || i.product?.price || 0) * (i.qty || 1), 0);
  const savings = Math.max(0, itemsMrp - combo.price);

  const handleAdd = () => {
    addItem({
      kind: "combo",
      ref_id: combo.id,
      name: combo.name,
      unit_price: combo.price,
      mrp: combo.mrp,
      image_url: currentImg,
      is_green_cracker: false,
    });
    toast.success(`${combo.name} added to cart`);
  };

  const handleWish = async () => {
    if (!user) { toast.info("Please sign in to save items"); navigate("/login"); return; }
    const res = await toggle(combo.id);
    if (res?.in_wishlist) toast.success("Added to wishlist");
    else toast("Removed from wishlist");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <button onClick={() => navigate(-1)} className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> Back
      </button>

      <div className="mt-3 grid md:grid-cols-2 gap-6">
        {/* Media */}
        <div className="space-y-3">
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-hyd-panel border border-hyd-border">
            {currentImg ? (
              <img src={currentImg} alt={combo.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-hyd-text3"><Gift className="w-16 h-16" /></div>
            )}
            {discount > 0 && (
              <div className="absolute top-3 left-3 bg-hyd-red text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                {discount}% OFF
              </div>
            )}
            <div className="absolute top-3 right-3 bg-hyd-gold text-hyd-bg text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
              Combo pack
            </div>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedIdx(i)}
                  className={`shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-colors ${selectedIdx === i ? "border-hyd-gold" : "border-hyd-border hover:border-hyd-gold/40"}`}
                >
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center bg-hyd-gold/15 text-hyd-gold">
              <Gift className="w-2.5 h-2.5 mr-1" /> Combo pack
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{combo.name}</h1>
          {combo.brand && <div className="text-hyd-text3 text-xs uppercase tracking-widest mt-1">{combo.brand}</div>}
          {combo.short_description && (
            <p className="text-hyd-text2 mt-3 text-sm">{combo.short_description}</p>
          )}

          <div className="mt-4 flex items-baseline gap-3">
            <div className="text-hyd-gold font-display text-3xl font-bold">{inr(combo.price)}</div>
            {combo.mrp > combo.price && <div className="text-hyd-text3 line-through">{inr(combo.mrp)}</div>}
            {discount > 0 && <div className="text-emerald-400 text-sm font-bold">Save {inr(combo.mrp - combo.price)}</div>}
          </div>
            <div className="text-xs text-hyd-text3">All-inclusive price · {items.length} products in this pack</div>

          <div className="mt-6 flex items-center gap-3">
            {qty > 0 ? (
              <div className="bg-hyd-panel border border-hyd-red rounded-full flex items-center px-2 py-1.5 gap-2">
                <button onClick={() => setQty("combo", combo.id, qty - 1)} className="w-8 h-8 grid place-items-center text-hyd-red rounded-full hover:bg-hyd-bg"><Minus className="w-4 h-4" /></button>
                <span className="min-w-[24px] text-center font-bold text-hyd-text">{qty}</span>
                <button onClick={() => setQty("combo", combo.id, qty + 1)} className="w-8 h-8 grid place-items-center text-hyd-gold rounded-full hover:bg-hyd-bg"><Plus className="w-4 h-4" /></button>
              </div>
            ) : (
              <Button data-testid="combo-detail-add" onClick={handleAdd} className="bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider h-11 rounded-full px-6">
                + Add to cart
              </Button>
            )}
            <button
              onClick={handleWish}
              className="w-11 h-11 rounded-full border border-hyd-border grid place-items-center hover:border-hyd-red transition-colors"
              aria-label="Toggle wishlist"
            >
              <Heart className={`w-4 h-4 ${has(combo.id) ? "fill-hyd-red text-hyd-red" : "text-hyd-text2"}`} />
            </button>
          </div>

          {/* Ribbons */}
          <div className="mt-6 grid grid-cols-2 gap-2 text-[11px]">
            <Ribbon icon={Truck} text="Min order value ₹2300" />
            <Ribbon icon={ShieldCheck} text="Age-restricted (18+)" />
            {savings > 0 && <Ribbon icon={Sparkles} text={`Save ${inr(savings)} vs buying alone`} highlight />}
            <Ribbon icon={Package} text="Ships in 24-48h after confirmation" />
          </div>

          {combo.description && (
            <div className="mt-6 bg-hyd-panel border border-hyd-border rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-hyd-gold mb-2">About this combo</div>
              <p className="text-hyd-text2 text-sm whitespace-pre-wrap leading-relaxed">{combo.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Items included */}
      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-hyd-gold" /> What&apos;s inside
          <span className="text-hyd-text2 text-sm font-normal">({items.length} products)</span>
        </h2>

        {items.length === 0 ? (
          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-6 text-center text-hyd-text3">
            This combo doesn&apos;t have any items configured yet.
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((it, i) => {
              const p = it.product;
              if (!p) return (
                <div key={i} className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 text-hyd-text3 text-sm">
                  Product unavailable · qty {it.qty}
                </div>
              );
              const pImg = imageUrl(p.images?.[p.primary_image_index || 0] || p.images?.[0] || "");
              return (
                <div
                  key={i}
                  data-testid={`combo-item-${p.id}`}
                  className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 flex gap-3 items-center"
                >
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-hyd-panel2 shrink-0 border border-hyd-border">
                    {pImg ? <img src={pImg} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm line-clamp-1">{p.name}</div>
                    <div className="text-xs text-hyd-text2 mt-1">
                      <span className="bg-hyd-bg border border-hyd-border rounded-full px-2 py-0.5 text-hyd-gold font-bold">× {it.qty}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Bundle savings summary */}
            <div className="bg-hyd-gold/10 border border-hyd-gold/40 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-hyd-gold font-bold">Bundle savings</div>
                <div className="text-sm text-hyd-text2">Individually: <span className="line-through">{inr(itemsMrp)}</span></div>
              </div>
              <div className="text-right">
                <div className="text-hyd-gold font-display text-2xl font-bold">{inr(combo.price)}</div>
                {savings > 0 && <div className="text-emerald-400 text-xs font-bold">You save {inr(savings)}</div>}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Ribbon({ icon: Icon, text, highlight }) {
  return (
    <div className={`flex items-center gap-2 rounded-2xl px-3 py-2 border ${highlight ? "bg-hyd-gold/10 border-hyd-gold/40 text-hyd-gold" : "bg-hyd-panel border-hyd-border text-hyd-text2"}`}>
      <Icon className={`w-4 h-4 ${highlight ? "text-hyd-gold" : "text-hyd-gold"}`} />
      <span>{text}</span>
    </div>
  );
}
